import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SupportThread } from "@/components/SupportFooter";

const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const FILTERS = [["open", "Open"], ["solved", "Solved"], ["all", "All"]];

export default function SuperAdminSupport() {
    const [filter, setFilter] = useState("open");
    const [data, setData] = useState({ items: [], open: 0, unread: 0 });
    const [sel, setSel] = useState(new URLSearchParams(window.location.search).get("id"));
    const [doc, setDoc] = useState(null);
    const load = useCallback(() => api.get("/super-admin/support", { params: { status: filter } }).then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load messages")), [filter]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!sel) return setDoc(null);
        api.get(`/super-admin/support/${sel}`).then(({ data: d }) => { setDoc(d); load(); window.dispatchEvent(new Event("sa-support-read")); }).catch(() => setSel(null));
    }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

    const reply = async (text) => {
        try {
            const { data: d } = await api.post(`/super-admin/support/${doc.id}/reply`, { message: text });
            setDoc(d);
            toast.success(d.emailed ? `Reply sent to ${d.from_email}` : "Reply saved in the app (email couldn't be sent)");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send the reply");
        }
    };
    const setStatus = async (status) => {
        try {
            const { data: d } = await api.patch(`/super-admin/support/${doc.id}`, { status });
            setDoc({ ...doc, ...d });
            toast.success(status === "solved" ? "Marked as solved" : "Reopened");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
        }
    };

    return (
        <div className="p-6 sm:p-10 max-w-7xl mx-auto" data-testid="sa-support-page">
            <h1 className="font-display font-extrabold text-4xl text-ink">Support inbox</h1>
            <p className="mt-1 text-sm text-ink/55">Questions from company admins. Replies go to their email and appear in their app.</p>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
                <div className="bg-white border border-ink/10">
                    <div className="flex items-center gap-1 p-2 border-b border-ink/10" data-testid="sa-support-filters">
                        {FILTERS.map(([k, l]) => (
                            <button key={k} onClick={() => setFilter(k)} data-testid={`sa-support-filter-${k}`}
                                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${filter === k ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                                {l}{k === "open" && data.open ? ` (${data.open})` : ""}
                            </button>
                        ))}
                    </div>
                    <div className="max-h-[65vh] overflow-y-auto" data-testid="sa-support-list">
                        {data.items.length === 0 && <div className="py-12 text-center text-sm text-ink/40 flex flex-col items-center gap-2"><Inbox className="w-6 h-6" /> Nothing here</div>}
                        {data.items.map((m) => (
                            <button key={m.id} onClick={() => setSel(m.id)} data-testid={`sa-support-item-${m.id}`}
                                className={`w-full text-left px-4 py-3 border-b border-ink/5 transition-colors ${sel === m.id ? "bg-klein/5" : "hover:bg-ink/[0.03]"}`}>
                                <div className="flex items-center gap-2">
                                    {m.unread_support && <span className="w-2 h-2 rounded-full bg-klein shrink-0" data-testid={`sa-support-unread-${m.id}`} />}
                                    <span className="text-sm font-semibold text-ink truncate flex-1">{m.subject}</span>
                                    <span className="text-[10px] text-ink/45 whitespace-nowrap">{when(m.updated_at || m.created_at)}</span>
                                </div>
                                <div className="text-xs text-ink/55 truncate">{m.company_name} · {m.topic}{m.status === "solved" ? " · Solved" : ""}</div>
                                <div className="mt-0.5 text-xs text-ink/45 truncate">{m.last_by === "support" ? "You: " : ""}{m.last_text}</div>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="bg-white border border-ink/10 p-5 sm:p-6 min-h-[50vh]" data-testid="sa-support-detail">
                    {!doc ? <div className="h-full flex items-center justify-center text-sm text-ink/40">Pick a message to read and reply</div> : (
                        <div className="space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="overline">{doc.topic}</div>
                                    <h2 className="font-display font-bold text-xl text-ink break-words">{doc.subject}</h2>
                                    <div className="text-xs text-ink/55 mt-1">{doc.company_name} ({doc.company_id}) · {doc.from_name} &lt;{doc.from_email}&gt;{doc.phone ? ` · ${doc.phone}` : ""}</div>
                                </div>
                                {doc.status === "solved" ? (
                                    <button onClick={() => setStatus("open")} className="inline-flex items-center gap-1.5 border border-ink/20 px-3 py-1.5 text-xs" data-testid="sa-support-reopen"><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>
                                ) : (
                                    <button onClick={() => setStatus("solved")} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1.5 text-xs" data-testid="sa-support-solve"><CheckCircle2 className="w-3.5 h-3.5" /> Mark solved</button>
                                )}
                            </div>
                            <SupportThread doc={doc} onReply={reply} mine="support" testid="sa-support-thread" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
